import React, { useState, useEffect } from 'react';
import { StageProps, RaceParticipant } from '../types';
import { AIService } from '../services/aiService';

const PointsApplication: React.FC<StageProps> = ({ appData, setAppData, onNext, onPrev }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedParticipants, setProcessedParticipants] = useState<RaceParticipant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    if (appData.processedRaceData) {
      setProcessedParticipants(appData.processedRaceData);
      setShowValidation(true);
    }
  }, [appData.processedRaceData]);

  const applyPoints = async () => {
    if (!appData.newRaceData?.participants || !appData.pointsScheme) {
      setError('Missing race data or points scheme');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setShowValidation(false);

    try {
      const processedData = await AIService.applyPointsScheme(
        appData.newRaceData.participants,
        appData.pointsScheme
      );

      setProcessedParticipants(processedData);
      setAppData({
        ...appData,
        processedRaceData: processedData
      });
      setShowValidation(true);
    } catch (error) {
      console.error('Points application failed:', error);
      setError(error instanceof Error ? error.message : 'Failed to apply points scheme');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePointsConfirmed = () => {
    if (processedParticipants) {
      setAppData({
        ...appData,
        processedRaceData: processedParticipants
      });
      onNext?.();
    }
  };

  const handlePointsNeedAdjustment = () => {
    // Return to points configuration to modify the scheme
    setShowValidation(false);
    setProcessedParticipants(null);
    setAppData({
      ...appData,
      processedRaceData: null
    });
    onPrev?.();
  };

  const handleReapplyPoints = () => {
    // Clear current results and reapply with the same scheme
    setShowValidation(false);
    setProcessedParticipants(null);
    setAppData({
      ...appData,
      processedRaceData: null
    });
    applyPoints();
  };

  const formatPoints = (points: number) => {
    return points % 1 === 0 ? points.toString() : points.toFixed(1);
  };

  if (showValidation && processedParticipants) {
    const participantsByCategory = processedParticipants.reduce((groups, participant) => {
      const category = participant.category || 'No Category';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(participant);
      return groups;
    }, {} as Record<string, RaceParticipant[]>);

    return (
      <div className="stage-container">
        <div className="stage-header">
          <h2>Review Points Assignment</h2>
          <p>Please review the points calculation below. Are these points correct?</p>
        </div>

        <div className="file-info">
          <h4>📊 Points Applied: {appData.pointsScheme}</h4>
          <p>
            Points have been calculated for <strong>{processedParticipants.length}</strong> participants
          </p>
        </div>

        <div style={{ marginBottom: '2rem' }}>
          {Object.entries(participantsByCategory).map(([category, participants]) => (
            <div key={category} className="category-section">
              <div className="category-header">
                <h4>{category}</h4>
                <span className="category-count">{participants.length}</span>
              </div>
              <div className="category-table">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Position</th>
                      <th>Name</th>
                      <th>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participants
                      .sort((a, b) => (a.position || 999) - (b.position || 999))
                      .map((participant, index) => (
                        <tr key={index}>
                          <td>{participant.position || 'N/A'}</td>
                          <td>{participant.name}</td>
                          <td className="points-cell">
                            <span className="points-value">{formatPoints(participant.points || 0)}</span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        <div className="validation-question">
          <h3 style={{ textAlign: 'center', marginBottom: '1.5rem', color: '#2d3748' }}>
            Are the points correct?
          </h3>
          
          <div className="validation-actions">
            <button 
              className="btn btn-secondary"
              onClick={handlePointsNeedAdjustment}
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              ← No, Modify Points Scheme
            </button>
            
            <button 
              className="btn btn-outline"
              onClick={handleReapplyPoints}
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              🔄 Reapply Same Scheme
            </button>
            
            <button 
              className="btn btn-primary"
              onClick={handlePointsConfirmed}
              style={{ 
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600'
              }}
            >
              Yes, Proceed → ✓
            </button>
          </div>
        </div>

        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <small style={{ color: '#718096' }}>
            💡 You can go back to modify the points scheme or proceed if the calculation looks correct
          </small>
        </div>
      </div>
    );
  }

  return (
    <div className="stage-container">
      <div className="stage-header">
        <h2>Apply Points Scheme</h2>
        <p>Your points scheme will be applied to the race results. Review and confirm the calculation.</p>
      </div>

      {appData.pointsScheme && (
        <div className="file-info">
          <h4>🏆 Points Scheme</h4>
          <p><strong>{appData.pointsScheme}</strong></p>
          {appData.newRaceData && (
            <p>This will be applied to <strong>{appData.newRaceData.participants.length}</strong> participants</p>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      <div className="stage-actions">
        <button className="btn btn-secondary" onClick={onPrev} disabled={isProcessing}>
          ← Back
        </button>
        
        {!processedParticipants ? (
          <button 
            className="btn btn-primary" 
            onClick={applyPoints}
            disabled={isProcessing || !appData.newRaceData?.participants || !appData.pointsScheme}
          >
            {isProcessing ? (
              <>
                <span className="loading-spinner"></span>
                <span>Calculating points...</span>
              </>
            ) : (
              '🔢 Calculate Points'
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default PointsApplication; 